import { Injectable } from '@angular/core';

import { Action, Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { switchMap, catchError, tap, withLatestFrom } from 'rxjs/operators';

import { AppState, Selectors, Actions as RootActions } from 'store/root-reducer';

import {
  GetAllAccounts, GetAccountsInCurrentDomain, GetAccountsInCurrentDomainSuccess, AdminActionTypes,
  UpdateRoles, GetAllAccountsSuccess, UpdateRolesSuccess, AccountBatchUpload, AccountBatchUploadSuccess,
  GetAllAccountsFailure, GetAccountsInCurrentDomainFailure, UpdateRolesFailure, AccountBatchUploadFailure,
  CreateAccount, CreateAccountSuccess, CreateAccountFailure, GetPermissions, GetPermissionsSuccess,
  GetPermissionsFailure,
} from 'store/actions/admin.actions';

import { Account } from 'modules/shared/models/account.models';
import { BaseListReqParams } from 'modules/shared/models/base.models';
import { Role } from 'modules/shared/models/role.models';
import { CurrentUser } from 'modules/shared/models/account.models';
import {
  AccountListResponse, AccountPayload, UpdateRolePayload, UpdateRoleRequest, AccountBatchUploadPayload,
  CreateAccountPayload, GroupPermissionResponse, UpdateRoleActionPayload
} from 'store/models/admin.models';
import { Domain } from 'modules/shared/models/domain.models';

import { ACCOUNT_PER_PAGE } from 'modules/shared/constants/account.constants';

import { AdminService } from 'modules/core/services/admin.service';
import { SnackbarService } from 'app/services/snackbar.service';

@Injectable()
export class AdminEffects {
  readonly accountPerPage: number = ACCOUNT_PER_PAGE;

  @Effect() GetAllAccounts: Observable<Action> = this.actions$.pipe(
    ofType<GetAllAccounts>(AdminActionTypes.GET_ALL_ACCOUNTS),
    switchMap((input: any) => {
      if (input.payload.searchName) {
        input.payload.name = input.payload.searchName;
        delete input.payload.searchName;
      }
      return this.adminService.getAccounts(<AccountPayload>input.payload)
        .pipe(
          switchMap((res: AccountListResponse) => {
            return of(new GetAllAccountsSuccess(res));
          }),
          catchError((err: any) => of(new GetAllAccountsFailure(err)))
        );
    }),
  );

  @Effect() GetAccountsInCurrentDomain: Observable<Action> = this.actions$.pipe(
    ofType<GetAccountsInCurrentDomain>(AdminActionTypes.GET_ACCOUNTS_IN_CURRENT_DOMAIN),
    withLatestFrom(this.store.select(Selectors.domain.getCurrentDomain)),
    switchMap(([input, currentDomain]: [GetAccountsInCurrentDomain, Domain]) => {
      if (!currentDomain) {
        return of({ type: 'EMPTY' });
      }

      const getAccountsPayload: AccountPayload = input.payload;

      if (getAccountsPayload.searchName) {
        getAccountsPayload.name = getAccountsPayload.searchName;
        delete getAccountsPayload.searchName;
      }

      getAccountsPayload.domain = currentDomain.domainName;

      return this.adminService.getAccounts(getAccountsPayload)
        .pipe(
          switchMap((res: AccountListResponse) => {
            return of(new GetAccountsInCurrentDomainSuccess(res));
          }),
          catchError((err: any) => of(new GetAccountsInCurrentDomainFailure(err)))
        );
    }),
  );

  @Effect() CreateUser: Observable<Action> = this.actions$.pipe(
    ofType<CreateAccount>(AdminActionTypes.CREATE_ACCOUNT),
    switchMap((input: any) => {
      return this.adminService
        .createAccount(<CreateAccountPayload>input.payload)
        .pipe(
          switchMap((res: Account) => of([res, input.payload])),
          switchMap(([res, payload]: [Account, CreateAccountPayload]) => {
            const updateUserPermissionsPayload: UpdateRolePayload = {
              oauthId: res.oauthId,
              roles: <any>payload.roles,
              updatedUserId: res.id,
              getAccountsInCurrentDomainPayload: payload.getAccountsInCurrentDomainPayload
            };

            delete payload.roles;
            delete payload.name;
            delete payload.getAccountsInCurrentDomainPayload;

            return [
              new CreateAccountSuccess(res),
              new UpdateRoles(updateUserPermissionsPayload)
            ];
          }),
          tap(() => this.snackbar.success('Account successfully created')),
          catchError((err: any) => {
            if (err && err.error && err.error.detail) {
              this.snackbar.error(err.error.detail);
            } else {
              this.snackbar.error('Failed to create account');
            }
            return of(new CreateAccountFailure(err));
          }),
        );
    }),
  );

  @Effect() UpdateRoles: Observable<Action> = this.actions$.pipe(
    ofType<UpdateRoles>(AdminActionTypes.UPDATE_ROLES),
    withLatestFrom(
      this.store.select(Selectors.auth.getUser),
      this.store.select(Selectors.domain.getCurrentDomain)
    ),
    switchMap(([input, user, currentDomain]: [UpdateRoles, CurrentUser, Domain]) => {
      return this.adminService
        .updateRoles(<UpdateRoleRequest>{
          oauthId: input.payload.oauthId,
          roles: input.payload.roles,
          updatedUserId: input.payload.updatedUserId
        })
        .pipe(
          switchMap((res: Role[]) => of([res, input.payload.oauthId])),
          switchMap(([res, oauthId]: [Role[], string]) => {
            // need to clean up getUsers in customer effect and get accounts related in admin effect
            // might be duplicate
            const actions$: Array<any> = [
              new UpdateRolesSuccess(<UpdateRoleActionPayload>{
                oauthId: oauthId,
                roles: res
              }),
              new RootActions.admin.GetAccountsInCurrentDomain(input.payload.getAccountsInCurrentDomainPayload),
              new RootActions.customer.GetUsers({
                limit: 999,
                offset: 0,
                domain: currentDomain.domainName
              })
            ];

            if (user.sub === input.payload.updatedUserId) {
              actions$.push(new RootActions.auth.GetUserRoles({ domainId: currentDomain.id }));
            }

            return actions$;
          }),
          tap(() => this.snackbar.success('User permissions successfully updated')),
          catchError((err: any) => {
            this.snackbar.error('Failed to update user permissions');
            return of(new UpdateRolesFailure(err));
          }),
        );
    }),
  );

  @Effect() GetPermissions: Observable<Action> = this.actions$.pipe(
    ofType<GetPermissions>(AdminActionTypes.GET_PERMISSIONS),
    switchMap((res: any) => {
      return this.adminService.getPermissions(<BaseListReqParams>res.payload)
        .pipe(
          switchMap((permissions: GroupPermissionResponse) => {
            return of(new GetPermissionsSuccess(permissions));
          }),
          catchError((err: any) => of(new GetPermissionsFailure(err)))
        );
    }),
  );

  @Effect() AccountBatchUpload: Observable<Action> = this.actions$.pipe(
    ofType<AccountBatchUpload>(AdminActionTypes.ACCOUNT_BATCH_UPLOAD),
    switchMap((input: any) => {
      return this.adminService
        .accountBatchUpload(<AccountBatchUploadPayload>(input.payload))
        .pipe(
          switchMap((res: Account[]) => of([res, input.payload])),
          switchMap(([res, payload]: [Account[], AccountBatchUploadPayload]) => [
            new AccountBatchUploadSuccess(res),
            new GetAllAccounts(<AccountPayload>payload)
          ]),
          tap(() => this.snackbar.success('Accounts successfully created via batch upload')),
          catchError((err: any) => {
            const error: string = err.error ? err.error.toString() : '';
            const detail: string = err.error.detail ? err.error.detail.toString() : '';
            if (error.startsWith('Validation')) {
              this.snackbar.error('Error: Emails from uploaded file have an invalid format');
            } else if (detail.startsWith('Domain')) {
              this.snackbar.error('Error: Domain from uploaded file is invalid');
            } else if (detail.startsWith('Validation')) {
              this.snackbar.error('Error: Emails from uploaded file already have existing accounts');
            } else if (detail.startsWith('Unsupported')) {
              this.snackbar.error('Error: Unsupported file type. Please upload .xlsx files');
            } else {
              this.snackbar.error('Error: Failed to create users via batch upload');
            }
            return of(new AccountBatchUploadFailure(err));
          }),
        );
    })
  );

  constructor(
    private actions$: Actions,
    private adminService: AdminService,
    private snackbar: SnackbarService,
    private store: Store<AppState>
  ) {}
}
